import { type ApiResponse, put } from "@src/appV2/api/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { getWorkerBookabilityPreferenceUrl } from "./utils";

const requestAttributesSchema = z.object({
  distanceInMiles: z.number().positive().max(150),
  autoBookingIsEnabled: z.boolean(),
});

const requestSchema = z.object({
  data: z.object({
    attributes: requestAttributesSchema,
    type: z.literal("bookability-preference"),
  }),
});

const attributesSchema = z.object({
  workerId: z.string(),
  autoBookingIsEnabled: z.boolean(),
  distanceInMiles: z.number(),
});

const responseSchema = z.object({
  data: z.object({
    attributes: attributesSchema,
    id: z.string(),
    type: z.string(),
  }),
});
export type PutWorkerBookabilityPreferenceRequest = z.infer<typeof requestSchema>;
export type PutWorkerBookabilityPreferenceResponse = z.infer<typeof responseSchema>;

export function usePutWorkerBookabilityPreference(
  workerId?: string,
  options: UseMutationOptions<
    ApiResponse<PutWorkerBookabilityPreferenceResponse>,
    AxiosError,
    PutWorkerBookabilityPreferenceRequest["data"]["attributes"]
  > = {}
): UseMutationResult<
  ApiResponse<PutWorkerBookabilityPreferenceResponse>,
  AxiosError,
  PutWorkerBookabilityPreferenceRequest["data"]["attributes"]
> {
  const url = getWorkerBookabilityPreferenceUrl(workerId ?? "");

  return useMutation({
    mutationFn: async ({ distanceInMiles, autoBookingIsEnabled }) => {
      return await put({
        url,
        data: {
          data: {
            type: "bookability-preference",
            attributes: {
              distanceInMiles,
              autoBookingIsEnabled,
            },
          },
        },
        requestSchema,
        responseSchema,
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PUT_WORKER_BOOKABILITY_PREFERENCE_API_FAILURE,
    },
    ...options,
  });
}
